<template>
  <div class="automated-email-filters-content-ui">
    <VTabs :tabs="tabs" toggle-manually swipeable>
      <template #tab-0="{ active }">
        <div v-if="active" class="automated-email-filters-content-ui__options">
          <div class="automated-email-filters-content-ui__options-item">
            <ListingsPriceFilter v-model="filters.price_range" :sale-type="filters.type" />
          </div>
          <div class="automated-email-filters-content-ui__options-item">
            <ListingsBedsFilter v-model="filters.bed_range" />
          </div>
          <div class="automated-email-filters-content-ui__options-item">
            <ListingsTypeFilter v-model="filters.category" />
          </div>
          <div class="automated-email-filters-content-ui__options-item">
            <ListingsFilterControl label="Location">
              <ListingsLocationSearch v-model="filters.location" />
            </ListingsFilterControl>
          </div>
          <div class="automated-email-filters-content-ui__options-item">
            <HiddenOpportunitiesFilter v-model="filters.hidden" />
          </div>
          <div class="automated-email-filters-content-ui__options-item">
            <ExcludeSoldSinceFilter :model-value="true" disabled />
          </div>
          <div class="automated-email-filters-content-ui__options-item">
            <ListingsCheckboxFilter
              v-model="excludeArchived"
              label="Exclude archived"
              explanation="Excludes archived opportunities from automated emails."
            />
          </div>
        </div>
      </template>
      <template v-if="isExternalCompany" #tab-1="{ active }">
        <keep-alive>
          <AutomatedEmailFiltersData
            v-if="active"
            v-model="filters"
            :opportunity-data-client="opportunityDataClient"
          />
        </keep-alive>
      </template>
      <template v-if="isExternalCompany" #tab-2="{ active }">
        <keep-alive>
          <ListingsFiltersMarketingConsent v-if="active" v-model="filters" />
        </keep-alive>
      </template>
    </VTabs>
    <div class="automated-email-filters-content-ui__actions">
      <div class="automated-email-filters-content-ui__clear-action">
        <VBtn flat @click="$emit('clear')">Clear all</VBtn>
        <VIconTooltip v-if="showClearTooltip" class="ghost" :icon-src="infoIcon">
          Resets all filters to company defaults
        </VIconTooltip>
      </div>
      <VBtn
        class="full-width"
        :disabled="isApplyDisabled || isSaving"
        :loading="isSaving"
        @click="$emit('apply')"
        >{{ applyButtonLabel }}</VBtn
      >
    </div>
  </div>
</template>

<script lang="ts" setup>
  // Types
  import type { TListingFilters, TOpportunityDataClientModel } from '~/misc/listings/types';
  import type { ITab } from '~/misc/v-tabs/types';
  import type { AbstractDataClient } from '~/misc/services/data-client';

  // Components
  import ListingsPriceFilter from '~/misc/listings/filters/price/ListingsPriceFilter.vue';
  import ListingsBedsFilter from '~/misc/listings/filters/beds/ListingsBedsFilter.vue';
  import ListingsTypeFilter from '~/misc/listings/filters/type/ListingsTypeFilter.vue';
  import ListingsLocationSearch from '~/misc/listings/location-search/ListingsLocationSearch.vue';
  import ListingsFilterControl from '~/misc/listings/filters/control/ListingsFilterControl.vue';
  import HiddenOpportunitiesFilter from '~/misc/listings/filters/hidden-opportunities/HiddenOpportunitiesFilter.vue';
  import ExcludeSoldSinceFilter from '~/misc/listings/filters/exclude-sold-since/ExcludeSoldSinceFilter.vue';
  import ListingsCheckboxFilter from '~/misc/listings/filters/checkbox/ListingsCheckboxFilter.vue';
  import AutomatedEmailFiltersData from '~/modules/opportunities/automated-email/settings/filters/data/AutomatedEmailFiltersData.vue';
  import ListingsFiltersMarketingConsent from '~/misc/listings/filters/marketing-consent/ListingsFiltersMarketingConsent.vue';
  import VBtn from '~/misc/v-btn';
  import VTabs from '~/misc/v-tabs';
  import VIconTooltip from '~/misc/v-icon-tooltip/VIconTooltip.vue';

  // Assets
  import infoIcon from '~/assets/images/icons/info-icon.svg';

  const props = withDefaults(
    defineProps<{
      tabs: ITab[];
      applyButtonLabel?: string;
      opportunityDataClient?: AbstractDataClient<TOpportunityDataClientModel>;
      isExternalCompany?: boolean;
      showClearTooltip?: boolean;
      isSaving?: boolean;
    }>(),
    {
      applyButtonLabel: 'Apply',
      opportunityDataClient: undefined,
    }
  );

  defineEmits<{
    (e: 'apply'): void;
    (e: 'clear'): void;
  }>();

  const filters = defineModel<TListingFilters>({ required: true });

  const excludeArchived: WritableComputedRef<boolean> = computed<boolean>({
    get(): boolean {
      return !!filters.value.exclude_archived;
    },
    set(value: boolean): void {
      filters.value.exclude_archived = Number(value);
    },
  });

  const isApplyDisabled: ComputedRef<boolean> = computed<boolean>(() => {
    if (!props.isExternalCompany) return false;

    return filters.value.contact_type.length === 0 || filters.value.marketing_consent.length === 0;
  });
</script>
